/** Dexie-backed operation packages for attendance-core (no HRF_LABOR_CORE). */

import { useQuery } from '@tanstack/react-query';

import {
  getAttendanceOpPackagePatternTaskCount,
  listAttendanceOpPackageInstancesByPattern,
  listAttendanceOpPackagePatterns,
  listAttendanceOpPackages,
} from '@/mocks/db/attendanceOpPackages';

import type { OperationPackage, OperationPackagePattern } from '../-types';

import { operationPackageQueryKeys } from './keys';

export type GetOperationPackagesRes = OperationPackage[];

export const getOperationPackages =
  async (): Promise<GetOperationPackagesRes> => {
    const rows = await listAttendanceOpPackages();
    return rows.map((r) => ({
      operationPackageId: r.operationPackageId,
      name: '',
      inProgressCount: r.inProgressCount,
    }));
  };

const getOperationPackagePatterns = async (
  packageId: string,
): Promise<OperationPackagePattern[]> => {
  const rows = await listAttendanceOpPackagePatterns(packageId);
  return rows.map((r) => ({
    operationPackagePatternId: r.operationPackagePatternId,
    name: '',
    nameKey: r.nameKey,
  }));
};

export const useGetOperationPackages = () =>
  useQuery({
    queryKey: operationPackageQueryKeys.list(),
    queryFn: getOperationPackages,
  });

export const useGetOperationPackagePatterns = (
  id: string,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: operationPackageQueryKeys.patterns(id),
    queryFn: () => getOperationPackagePatterns(id),
    enabled: (options?.enabled ?? true) && Boolean(id),
  });

export const useGetOperationTaskCountInProgress = (params: {
  operationPackageId: string;
  patternId: string;
}) =>
  useQuery({
    queryKey: operationPackageQueryKeys.patternCountInProgress(
      params.operationPackageId,
      params.patternId,
    ),
    queryFn: () =>
      getAttendanceOpPackagePatternTaskCount(
        params.operationPackageId,
        params.patternId,
      ),
    enabled: Boolean(params.patternId),
  });

export const useGetOperationPackageInstances = (
  patternId: string,
  options?: {
    enabled?: boolean;
    page?: number;
    pageSize?: number;
    targetId?: string;
  },
) => {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const targetId = options?.targetId ?? '';
  return useQuery({
    queryKey: operationPackageQueryKeys.instances(
      patternId,
      page,
      pageSize,
      targetId,
    ),
    queryFn: () =>
      listAttendanceOpPackageInstancesByPattern(patternId, {
        page,
        pageSize,
        targetId: options?.targetId,
      }),
    enabled: (options?.enabled ?? true) && Boolean(patternId),
  });
};
